import {
  attachmentApplied,
  pickAttachmentFields
} from "features/calls/dialstack/call-attachments.slice";
import {
  callHidden,
  callUnhidden
} from "features/calls/dialstack/hidden-calls.slice";

import { emptySplitApi } from "./empty-split-api";
import type { AssignedTag } from "./types/assigned-tag.type";

export type RecordingSettings = {
  recording_enabled: boolean;
  consent_acknowledged: boolean;
  consent_acknowledged_at: string | null;
  consent_acknowledged_by: number | null;
};

// A custom mailbox greeting (greeting_type 'unavailable'): the signed-in
// user's own, or a shared box's (ENG-2525). `url` is a short-lived signed
// link — always fetched fresh, never cached.
export type VoicemailGreeting = {
  owner: string;
  greeting_type: "unavailable";
  format: string;
  duration_seconds: number;
  size_bytes: number;
  url: string;
  updated_at: string;
};

export type UpdateRecordingSettingsRequest = {
  practiceId: number;
  acknowledge_consent?: boolean;
  recording_enabled?: boolean;
};

// Short-lived session minted server-side; the sk_* secret never reaches the browser.
export type DialstackSession = {
  account?: string;
  client_secret: string;
  expires_at?: string;
};

export type CreateAccountSessionRequest = {
  practice_id: number;
  components?: Record<string, { enabled: boolean }>;
};

export type DialstackCall = {
  id: number;
  direction: "inbound" | "outbound";
  from_number: string;
  to_number: string;
  patient: number | null;
  matched_on: string | null;
  user: number | null;
  status: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  ai_summary: string | null;
  summary_status: "pending" | "ready" | "none";
  created_at: string;
  // Only present on the per-patient list (PatientCallSerializer).
  transcript?: string | null;
  // CNAM caller-ID name from the provider ("" when none was delivered).
  from_name?: string;
  // Free text set when a staff member attached the call to a patient
  // ("caretaker", "called on behalf of") — "" otherwise.
  caller_relationship?: string;
  attached_at?: string | null;
  // The note staff typed on the call (ENG-2226); "" when none. Undefined
  // until the backend exposing it is deployed.
  staff_note?: string;
  staff_note_at?: string | null;
  // Tags on the call (ENG-2176). Absent on the outbound-call responses.
  assigned_tags?: AssignedTag[];
  // Hidden from the practice (ENG-2364): on every call payload, and only ever
  // set on a row Suno receives.
  hidden_at?: string | null;
};

// What kind of outside party a contact is (ENG-2177). Drives the avatar tint
// and the Rolodex filter chips; `family` is PHI-adjacent and blurs in privacy
// mode like a patient.
export type ContactKind =
  | "insurer"
  | "vendor"
  | "referral_partner"
  | "pharmacy"
  | "family"
  | "other";

// Non-patient record (insurer, vendor, family member) a call can match to.
export type CallContact = {
  id: number;
  name: string;
  organization: string;
  // Absent on a backend that predates the Rolodex API.
  kind?: ContactKind;
};

export type ContactPhoneNumber = {
  id: number;
  // Ten bare digits.
  number: string;
  label: string;
};

export type ContactStaffUser = {
  id: number;
  first_name: string;
  last_name: string;
};

export type DialstackContact = CallContact & {
  kind: ContactKind;
  email: string;
  notes: string;
  phone_numbers: ContactPhoneNumber[];
  // The requester's own speed dial (per user, not practice-wide).
  is_favorite: boolean;
  last_call_at: string | null;
  created_by: ContactStaffUser | null;
  updated_by: ContactStaffUser | null;
  created_at: string;
  updated_at: string;
};

export type ContactPhoneNumberInput = { number: string; label?: string };

export type ContactInput = {
  name: string;
  organization?: string;
  kind?: ContactKind;
  email?: string;
  notes?: string;
  // Any formatting; the backend keeps the last ten digits.
  phone_numbers: ContactPhoneNumberInput[];
};

// Partial update. `phone_numbers`, when sent, is the FULL list: numbers left
// out are removed, new ones added (ids are ignored). Omit it to leave the
// numbers alone.
export type ContactUpdateInput = { id: number } & Partial<ContactInput>;

export type ContactOrdering = "name" | "-last_call_at" | "-created_at";

export type GetContactsRequest = {
  search?: string;
  kind?: ContactKind;
  // true = only the requester's speed dial.
  favorite?: boolean;
  ordering?: ContactOrdering;
  limit?: number;
  offset?: number;
};

export type MergeContactsRequest = {
  // The contact that survives; the source is folded into it and removed.
  targetId: number;
  source_id: number;
};

// The same filters as the list; the response is the CSV itself.
export type ExportContactsRequest = Pick<
  GetContactsRequest,
  "search" | "kind" | "favorite" | "ordering"
>;

// One line per row the import could not use; `row` is the 1-based line
// number in the file (the header is line 1).
export type ContactImportSkippedRow = { row: number; reason: string };

export type ContactImportResult = {
  created: number;
  skipped: ContactImportSkippedRow[];
};

export type SetContactFavoriteRequest = {
  contactId: number;
  favorite: boolean;
};

// Exactly one of patient_id / contact_id / new_contact.
export type AttachCallRequest = {
  callId: number;
  patient_id?: number;
  contact_id?: number;
  // `kind` only on a backend with the Rolodex API — older deploys reject
  // unknown keys, so callers send it only when the Rolodex gate is on.
  new_contact?: {
    name: string;
    organization?: string;
    notes?: string;
    kind?: ContactKind;
  };
  relationship?: string;
  remember_number?: boolean;
};

// 409 body when the remote number already belongs to another contact.
export type NumberBelongsToContactError = {
  code: "number_belongs_to_contact";
  contact: CallContact;
};

export type GetPatientCallsRequest = {
  patientId: number;
  limit: number;
  offset: number;
};

export type PracticeCallPatient = {
  id: number;
  first_name: string;
  last_name: string;
  preferred_name: string;
};

// --- Call content insight (ENG-2126) --------------------------------------
export type CallerType =
  | "new_patient_lead"
  | "existing_patient"
  | "caregiver"
  | "vendor_business"
  | "spam"
  | "unknown";

export type CallIntent =
  | "book_appointment"
  | "reschedule_or_cancel"
  | "device_support"
  | "question"
  | "billing_insurance"
  | "results_or_records"
  | "sales_or_solicitation"
  | "other";

export type CallOutcome =
  | "appointment_booked"
  | "appointment_offered_not_booked"
  | "resolved_on_call"
  | "needs_follow_up"
  | "transferred"
  | "voicemail_left"
  | "no_conversation";

export type CallConversion =
  | "booked_on_call"
  | "booked_later"
  | "not_booked"
  | "not_applicable";

// The compact insight on list rows. Enum fields are "" until classified.
export type CallInsightSummary = {
  status: "pending" | "ready" | "none" | "failed";
  source: "heuristic" | "llm" | "staff" | "";
  caller_type: CallerType | "";
  primary_intent: CallIntent | "";
  outcome: CallOutcome | "";
  needs_action: boolean;
  action_resolved_at: string | null;
  is_opportunity: boolean;
  conversion: CallConversion | "";
  confidence: number | null;
};

export type CallInsightEvidence = { speaker: string; quote: string };

// The full insight on the call detail, evidence included.
export type CallInsight = CallInsightSummary & {
  intents: CallIntent[];
  action_summary: string;
  action_deadline_hint: "today" | "this_week" | "none" | "";
  action_resolved_how: "dismissed" | "outbound_call" | "appointment" | "";
  opportunity_reason: string;
  appointment: number | null;
  booked_at: string | null;
  evidence: CallInsightEvidence[];
  staff_corrected_at: string | null;
  corrections: Record<string, { from: unknown; to: unknown }>;
  prompt_version: string;
  updated_at: string;
};

// GET /dialstack/calls/insights/ — everything the Call Intelligence page draws
// (ENG-2126). Content sections are inbound-only; coverage counts every call.
export type CallInsightsCoverage = {
  calls: number;
  inbound_calls: number;
  transcribed: number;
  classified: number;
  llm_classified: number;
  staff_corrected: number;
  heuristic_only: number;
  failed: number;
};

export type CallFlowStage =
  | "caller_type"
  | "intent"
  | "outcome"
  | "appointment";

export type CallFlowNode = {
  id: string;
  stage: CallFlowStage;
  key: string;
  calls: number;
};

export type CallFlowLink = { source: string; target: string; calls: number };

export type CallerMixRow = {
  caller_type: CallerType;
  calls: number;
  answered: number;
  missed: number;
  minutes: number;
};

export type CallIntentRow = {
  intent: CallIntent;
  calls: number;
  share: number;
  by_week: { week: string; calls: number }[];
};

export type CallFunnel = {
  inbound: number;
  opportunity_calls: number;
  booked_on_call: number;
  booked_later: number;
  not_booked: number;
  pending: number;
  opportunity_appointments: number;
  completed: number;
  converted: number;
  booking_rate: number | null;
  median_time_to_book_s: number | null;
};

export type CallConversionByUserRow = {
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  opportunity_calls: number;
  booked: number;
  booking_rate: number | null;
  open_actions: number;
};

export type CallConversionByClinicRow = {
  clinic_id: number | null;
  clinic_name: string | null;
  opportunity_calls: number;
  booked: number;
  booking_rate: number | null;
};

export type CallFollowUpItem = {
  call_id: number;
  caller: string;
  from_number: string;
  caller_type: CallerType | "";
  action_summary: string;
  action_deadline_hint: "today" | "this_week" | "none" | "";
  started_at: string | null;
  age_s: number | null;
  user_id: number | null;
  clinic_id: number | null;
  patient_id: number | null;
};

export type CallNoiseNumber = {
  from_number: string;
  from_name: string | null;
  caller_type: CallerType | "";
  calls: number;
  last_seen_at: string | null;
};

export type CallHeatCell = {
  // ISO weekday, 1 = Monday.
  weekday: number;
  hour: number;
  calls: number;
  opportunity_calls: number;
  missed: number;
};

export type DialstackCallInsights = {
  coverage: CallInsightsCoverage;
  flow: { nodes: CallFlowNode[]; links: CallFlowLink[] };
  caller_mix: CallerMixRow[];
  intents: CallIntentRow[];
  outcomes: Record<CallOutcome, number>;
  funnel: CallFunnel;
  by_user: CallConversionByUserRow[] | null;
  by_clinic: CallConversionByClinicRow[];
  follow_ups: { open: number; overdue: number; items: CallFollowUpItem[] };
  noise: {
    spam_calls: number;
    vendor_calls: number;
    minutes: number;
    no_conversation: number;
    top_numbers: CallNoiseNumber[];
  };
  heat: CallHeatCell[];
  sentiment: null;
};

// Staff says the classifier was wrong; every field optional, at least one.
export type CorrectCallInsightRequest = {
  callId: number;
  caller_type?: CallerType;
  primary_intent?: CallIntent;
  outcome?: CallOutcome;
  needs_action?: boolean;
  is_opportunity?: boolean;
};

export type PracticeCallStaffUser = {
  id: number;
  first_name: string;
  last_name: string;
};

// Practice-level rows nest the patient for display and carry the recording indicator.
export type PracticeCall = Omit<DialstackCall, "patient" | "transcript"> & {
  patient: PracticeCallPatient | null;
  // Undefined until the backend exposing it is deployed.
  contact?: CallContact | null;
  // The clinic the call was resolved to (ENG-1996); null when none owns the
  // number. Undefined on a backend that predates the field.
  clinic?: number | null;
  has_recording: boolean;
  // Practice-wide "handled" mark for the needs-follow-up state (ENG-1809).
  // Undefined until the backend exposing it is deployed.
  follow_up_dismissed_at?: string | null;
  // Content insight (ENG-2126): null until classified; undefined on an older backend.
  insight?: CallInsightSummary | null;
  // Hidden from the practice (ENG-2364). Always null on a row a practice
  // user receives — they never get a hidden row; set on Suno's Hidden list.
  hidden_at?: string | null;
  hidden_by?: PracticeCallStaffUser | null;
  // Who filed the call under its patient or contact by hand (ENG-2563); null
  // on auto-matched rows, absent on a backend that predates it.
  attached_by?: PracticeCallStaffUser | null;
};

// The voicemail left on a call, nested on the practice detail (ENG-1809).
// Practice-visible: carries no is_read/user (the owner's personal mailbox state).
export type CallVoicemail = {
  id: number;
  from_name: string;
  duration_seconds: number | null;
  // The Heard state (ENG-2524) is practice-visible: everyone who can see the
  // call sees the checkmark. Absent on a backend that predates it.
  is_read?: boolean;
  heard_by?: PracticeCallStaffUser | null;
  heard_at?: string | null;
  transcript: string | null;
  transcript_status: "pending" | "ready" | "none";
  ai_summary: string | null;
  summary_status: "pending" | "ready" | "none";
  received_at: string | null;
};

// Detail nests the answering staff user (names the [Local] transcript speaker);
// a backend that predates the nesting returns the bare id, so tolerate both.
export type PracticeCallDetail = Omit<PracticeCall, "user" | "insight"> & {
  transcript: string | null;
  user: PracticeCallStaffUser | number | null;
  // Undefined until the backend exposing it is deployed.
  voicemail?: CallVoicemail | null;
  // Who typed the note on the call (ENG-2226).
  staff_note_by?: PracticeCallStaffUser | null;
  // Full content insight with evidence (ENG-2126); undefined on an older backend.
  insight?: CallInsight | null;
  // Who removed a manual attachment, and when (ENG-2563); null until one was.
  detached_by?: PracticeCallStaffUser | null;
  detached_at?: string | null;
  // The caller's number sits in the patient's secondary phone — the slot
  // "Remember number" writes — so Remove from patient can offer to clear it.
  number_remembered_on_patient?: boolean;
};

export type DetachCallRequest = {
  callId: number;
  // Also clear the number from the patient's secondary phone (ENG-2563).
  forget_number?: boolean;
};

export type DetachCallResponse = {
  call: PracticeCallDetail;
  number_forgotten: boolean;
};

export type SetCallNoteRequest = { callId: number; text: string };
export type SetCallNoteResponse = { call: PracticeCallDetail };

export type AttachCallResponse = {
  call: PracticeCallDetail;
  // false when the patient already had the number or the secondary slot was taken.
  number_remembered: boolean;
};

// The fields a manual attach changes on a call row — the only ones the
// attachment overlay may merge into list rows (see `call-attachments.slice.ts`).
export type CallAttachmentFields = {
  patient: PracticeCallPatient | null;
  contact: CallContact | null;
  matched_on: string | null;
  caller_relationship: string;
  attached_at: string | null;
  attached_by?: PracticeCallStaffUser | null;
};

export type GetPracticeCallsRequest = {
  limit?: number;
  offset?: number;
  direction?: "inbound" | "outbound";
  status?: string;
  patient_id?: number;
  from_date?: string;
  to_date?: string;
  // Free-text caller search (patient names / numbers). ENG-1807; older
  // backends ignore it.
  search?: string;
  // Calls matched or attached to one Rolodex contact (ENG-2177).
  contact_id?: number;
  // Comma-separated tag ids; calls carrying any of them.
  assigned_tags?: string;
  // Content filters (ENG-2126): comma lists of enum values / booleans; older
  // backends ignore them.
  caller_type?: CallerType[];
  intent?: CallIntent[];
  outcome?: CallOutcome[];
  conversion?: CallConversion[];
  needs_action?: boolean;
  is_opportunity?: boolean;
  // Suno only (ENG-2364): the practice's hidden calls instead of the visible
  // ones. Ignored by the backend for everyone else.
  hidden?: boolean;
};

// The in-call "Private" toggle (ENG-2364) names the call by the provider id
// the SDK carries; the backend falls back to the user's live call.
export type LiveCallRequest = { dialstack_call_id?: string };

// Practice-local hour bucket; sparse — hours with no calls are omitted.
export type CallsByHourRow = {
  hour: number;
  total_calls: number;
  answered_calls: number;
  missed_calls: number;
};

// One hour × one location, for the chart's "Split by location" view; sparse
// like CallsByHourRow. The location's name comes from calls_by_clinic.
export type CallsByHourByClinicRow = CallsByHourRow & {
  clinic_id: number | null;
};

// One hour × one staff member (admin-only). Counts match CallsByUserRow:
// answered is inbound answered, and there is no missed count.
export type CallsByHourByUserRow = {
  hour: number;
  user_id: number | null;
  total_calls: number;
  answered_calls: number;
  outbound_calls: number;
};

export type CallsByClinicRow = {
  // null = calls not assigned to any clinic (the "Unassigned" bucket).
  clinic_id: number | null;
  clinic_name: string | null;
  total_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  answered_calls: number;
  missed_calls: number;
  abandoned_calls: number;
  forwarded_calls: number;
  // 0–1 fraction; null when the row has no inbound calls.
  answer_rate: number | null;
};

export type CallsByUserRow = {
  // null = calls with no staff attribution.
  user_id: number | null;
  first_name: string | null;
  last_name: string | null;
  total_calls: number;
  // Inbound answered only — no per-user answer rate exists (missed calls
  // carry no user attribution), so there is deliberately no missed count here.
  answered_calls: number;
  outbound_calls: number;
  average_call_duration_in_s: number;
};

// Mirrors the retired suno-comms /metrics/ payload so the Calls Overview
// widgets consume it unchanged. The ENG-1931 keys are optional so this
// frontend can ship ahead of the backend that adds them.
export type DialstackCallMetrics = {
  metrics: {
    total_calls: number;
    answered_calls: number;
    missed_calls: number;
    average_call_duration_in_s: number;
    calls_by_duration: {
      short: number;
      medium: number;
      long: number;
      very_long: number;
    };
    monthly_call_status: {
      month: string;
      status: "incoming" | "outgoing";
      count: number;
    }[];
    monthly_call_duration: {
      month: string;
      duration: "short" | "medium" | "long" | "very_long";
      count: number;
    }[];
    // ENG-1931 additions ↓
    inbound_calls?: number;
    outbound_calls?: number;
    voicemail_calls?: number;
    // Abandoned = inbound, unanswered, no voicemail; missed = abandoned + voicemail.
    abandoned_calls?: number;
    // Forwarded = inbound calls transferred to another extension/number.
    forwarded_calls?: number;
    // 0–1 fraction; null when the window has no inbound calls.
    answer_rate?: number | null;
    calls_by_hour?: CallsByHourRow[];
    calls_by_hour_by_clinic?: CallsByHourByClinicRow[];
    // null for non-admins, like calls_by_user.
    calls_by_hour_by_user?: CallsByHourByUserRow[] | null;
    calls_by_clinic?: CallsByClinicRow[];
    // null for non-admins (per-employee metrics are owner/admin-only).
    calls_by_user?: CallsByUserRow[] | null;
  };
};

export type GetDialstackCallMetricsRequest = {
  // after_date inclusive, before_date exclusive (practice-local days).
  // Optional because getPrevDates() yields no dates until a range is picked;
  // consumers skip the query in that state.
  after_date?: string;
  before_date?: string;
  // Sent comma-separated; non-admins may only pass their own id as `user`.
  clinic?: number[];
  user?: number[];
  // Cache key only — the backend scopes by the authenticated practice; without
  // this a practice switch would serve another practice's cached numbers.
  practice_id?: number;
};

export type ExportDialstackCallsRequest = {
  from_date?: string;
  to_date?: string;
  clinic?: number[];
  user?: number[];
};

// The signed-in user's mailbox row (per-user; the backend scopes by owner).
export type Voicemail = {
  id: number;
  from_number: string;
  from_name: string;
  duration_seconds: number | null;
  // Only the Heard button sets `is_read` (ENG-2524) — playing never does.
  // `heard_by`/`heard_at` say who pressed it and when; null on rows marked
  // before the button existed. Absent on a backend that predates them.
  is_read: boolean;
  heard_by?: PracticeCallStaffUser | null;
  heard_at?: string | null;
  patient: PracticeCallPatient | null;
  contact?: CallContact | null;
  matched_on: string | null;
  call: number | null;
  transcript: string | null;
  transcript_status: "pending" | "ready" | "none";
  ai_summary: string | null;
  summary_status: "pending" | "ready" | "none";
  received_at: string | null;
  created_at: string;
  // The shared voicemail box the row sits in (ENG-2490); null for a personal
  // row and for a shared row whose box is not resolved. Absent on a backend
  // that predates it.
  shared_box?: { id: number; name: string } | null;
};

export type GetVoicemailsRequest = {
  limit?: number;
  offset?: number;
  unread?: boolean;
  patient_id?: number;
  from_date?: string;
  to_date?: string;
  search?: string;
  // true = every shared voicemail box the signed-in user may read, instead
  // of their own mailbox.
  shared?: boolean;
  // One shared box (ENG-2490); 403 for a box the user may not read.
  box?: number;
  contact_id?: number;
};

// A shared voicemail box the signed-in user may read (ENG-2490). DialStack
// owns the box; who may read it is Suno's rule: everyone at the practice
// (`restricted: false`, the default) or `members` only. Practice admins always
// see every box.
export type SharedVoicemailBox = {
  id: number;
  name: string;
  restricted: boolean;
  members: { id: number; first_name: string; last_name: string }[];
  unread_count: number;
};

export type OutboundCallContextRequest = {
  dial_string: string;
  patient_id: number;
};

export type OutboundPatientMatch = {
  patient_ids: number[];
  matched_on: string | null;
  // The contact owning the number, only when no patient does (ENG-2177).
  // Absent on a backend that predates it.
  contact?: CallContact | null;
};

// One of the practice's DialStack numbers a call may present as its caller ID
// (active, outbound-enabled), joined to the clinic that owns it (ENG-1938).
export type OutboundCallerId = {
  id: string;
  phone_number: string;
  caller_id_name: string | null;
  clinic_id: number | null;
  clinic_name: string | null;
};

// Which of the user's devices an outbound call is meant for (ENG-2183). The
// platform rings every device either way; this is intent, so the widget knows
// whether to narrate the call (desk phone) or leave it to the SDK's own leg.
export type DialDevice = "softphone" | "deskphone";

// A hardware phone assigned to this user in DialStack (ENG-2183). `online` is
// the provider's live registration state — false means it is unplugged or
// unreachable right now, so a dial meant for it would ring nothing.
export type UserDevice = {
  id: string;
  name: string | null;
  type: "deskphone" | "dect_handset";
  online: boolean;
  // The phone is the user's through a hot-desk session, not a permanent line
  // (ENG-2293). Older backends omit it.
  logged_in?: boolean;
};

// --- Hot desking (ENG-2293) --------------------------------------------------
// DialStack owns the session (an overlay on the phone's permanent assignment,
// with its own expiry, keypad login and webhooks); the backend mirrors it and
// adds who to tell, the 3 AM reset and the history.

export type DeskPhonePerson = {
  dialstack_user_id: string;
  // The Suno user when the backend knows them; null for a portal-only user.
  user_id: number | null;
  name: string;
};

export type DeskPhoneSessionInfo = DeskPhonePerson & {
  // Null when the backend first saw the session already running.
  started_at: string | null;
  // DialStack's own expiry; the backend ends logins overnight before it.
  expires_at: string | null;
};

export type DeskPhone = {
  id: string;
  name: string;
  type: "deskphone" | "dect_handset";
  location: string | null;
  // Null when the phone has no assigned line: the platform derives
  // reachability from lines, so an unassigned phone cannot be read.
  online: boolean | null;
  // The permanent assignment, hidden while a session is on the phone.
  owners: DeskPhonePerson[];
  // Who is logged in right now, or null.
  session: DeskPhoneSessionInfo | null;
  // The person whose calls ring here is on a call.
  on_call: boolean;
  // My calls ring here: I am logged in on it, or it is mine and nobody else is.
  mine: boolean;
  // Permanently assigned to me.
  own: boolean;
};

export type MyDeskPhoneSession = {
  device_id: string;
  device_name: string;
  started_at: string | null;
  expires_at: string | null;
};

export type DeskPhones = {
  phones: DeskPhone[];
  me: {
    own_phone_id: string | null;
    session: MyDeskPhoneSession | null;
  };
  policy: { ends: "overnight"; local_hour: number };
};

export type DeskPhoneSessionEnd =
  | ""
  | "logged_out"
  | "expired"
  | "replaced"
  | "moved"
  | "reset"
  | "unknown";

export type DeskPhoneSessionVia = "suno_app" | "provider" | "unknown" | "";

export type DeskPhoneHistoryRow = {
  id: number;
  device_id: string;
  device_name: string;
  user: { id: number; name: string } | null;
  user_name: string;
  dialstack_user_id: string;
  started_at: string | null;
  expires_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  started_via: DeskPhoneSessionVia;
  ended_via: DeskPhoneSessionVia;
  end_reason: DeskPhoneSessionEnd;
  end_detail: { by_name?: string; to_device_name?: string };
  is_open: boolean;
};

export type DeskPhoneHistoryQuery = {
  device_id?: string;
  user_id?: number;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
};

export type OutboundCallerIds = {
  numbers: OutboundCallerId[];
  // The number the platform presents when a call names none — the user's
  // standing caller ID, else the account default — or null when the
  // platform would pick some other active number and we cannot tell which.
  standing_id: string | null;
  devices: UserDevice[];
};

export type InitiateOutboundCallRequest = {
  dial_string: string;
  // The DialStack number id (`did_…`) to present, from `OutboundCallerIds`.
  // Omitted, the platform presents the standing caller ID.
  did?: string;
  device?: DialDevice;
  patient_id?: number;
};

// A colleague who can take a transfer. `extension` is what gets dialed — the
// softphone strips a destination down to dial characters, so never a user id.
export type VoiceDirectoryEntry = {
  name: string;
  extension: string;
  // The phone's own state: Do Not Disturb outranks the line. Null when it
  // could not be read.
  presence: VoicePresence | null;
  // What to show (ENG-2022): the phone's state, or what Suno knows on top of
  // it. Absent on a backend that predates it, so fall back to `presence`.
  status?: VoiceStatus | null;
  // Their DialStack user id: a leg placed from their web softphone carries
  // `user_<id>-wrtc` instead of the extension (ENG-2061). Absent on a backend
  // that predates it.
  user_id?: string;
};

// "mobile": nothing registered, but a push can still wake their phone.
export type VoicePresence =
  | "available"
  | "on_call"
  | "dnd"
  | "mobile"
  | "offline";

export type VoiceStatus =
  | VoicePresence
  | "with_patient"
  | "in_meeting"
  | "recording";

// Suno-only setup endpoints (Administration -> Voice Setup). Every mutation
// responds with a fresh status payload.
export type VoiceAccountTosStatus = "signed" | "unsigned" | "not_required";

export type VoiceAccountOnboardingStatus =
  | "not_invited"
  | "awaiting_acceptance"
  | "in_progress"
  | "complete";

export type VoiceFeaturesRequest = {
  call_insights_enabled?: boolean;
  hot_desking_enabled?: boolean;
};

export type VoiceSetupStatus = {
  practice: {
    id: number;
    name: string;
    voice_enabled: boolean;
    dialstack_account_id: string | null;
    recording_enabled: boolean;
    consent_acknowledged: boolean;
    consent_acknowledged_at: string | null;
    // Suno-only practice switches (ENG-2126, ENG-2293), flipped from the
    // Features card. `hot_desking_enabled` is absent until the backend that
    // owns it is deployed, so the card hides that row rather than lie.
    call_insights_enabled: boolean;
    hot_desking_enabled?: boolean;
  };
  environment: {
    secret_key_configured: boolean;
    webhook_secret_configured: boolean;
    base_url: string;
    // ENG-2126: the environment-wide kill switch for call classification
    // (DIALSTACK_CALL_INSIGHTS_ENABLED) and the model it runs on.
    call_insights_enabled?: boolean;
    call_insight_model?: string | null;
    // ENG-1916: the webhook sink this environment registers with the provider.
    webhook_url?: string | null;
    webhook_registered?: boolean | null;
  };
  account: {
    id: string;
    name: string | null;
    email: string | null;
    onboarding_complete: boolean | null;
    tos_accepted: boolean | null;
    // ENG-1916: derived from the provider's live account record.
    tos_status?: VoiceAccountTosStatus | null;
    onboarding_status?: VoiceAccountOnboardingStatus | null;
    main_location?: string | null;
    temporary_number?: { number: string; expires_at: string | null } | null;
    error: string | null;
  } | null;
  dialstack_users:
    | { id: string; name: string | null; email: string | null }[]
    | null;
  dialstack_users_truncated: boolean;
  users: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    voice_enabled: boolean;
    dialstack_user_id: string | null;
  }[];
  clinics: {
    id: number;
    name: string;
    dialstack_phone_number: string | null;
  }[];
};

export type GrantVoiceAccessRequest = {
  userId: number;
  mode: "create" | "link";
  dialstack_user_id?: string;
};

// ENG-1916: create the provider account from Suno (Suno-only). Rates are in
// cents. The response is the fresh status payload plus the webhook
// registration outcome; `signing_secret` is present only when the endpoint
// was created by this call and is never shown again.
export type VoiceSetupProvisionAddress = {
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: "US" | "CA" | "MX";
  address_number?: string;
  unit?: string;
};

export type VoiceSetupProvisionRequest = {
  business_name: string;
  owner_email: string;
  primary_contact_name: string;
  address: VoiceSetupProvisionAddress;
  per_user_rate: number;
};

export type VoiceSetupProvisionResponse = VoiceSetupStatus & {
  webhook: {
    registered: boolean | null;
    created: boolean;
    signing_secret: string | null;
  };
};

// ENG-1916 number porting (Suno-only). The backend mirrors each DialStack
// port order into a row and refreshes it on read + from a poller (no port
// webhooks); `provider` on detail responses is the raw DialStack order.
export type PortOrderStatus =
  | "draft"
  | "approved"
  | "submitted"
  | "exception"
  | "foc"
  | "complete"
  | "cancelled";

export type PortEligibilityResult = {
  portable_numbers: {
    phone_number: string;
    losing_carrier_name?: string;
    losing_carrier_spid?: string;
    is_wireless: boolean;
    account_number_required: boolean;
  }[];
  non_portable_numbers: {
    phone_number: string;
    rate_center?: string;
    city?: string;
    state?: string;
  }[];
};

export type PortOrderAddress = {
  house_number: string;
  street_name: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
};

export type PortOrderSubscriber = {
  btn: string;
  business_name: string;
  approver_name: string;
  account_number?: string;
  pin?: string;
  address: PortOrderAddress;
};

export type CreatePortOrderRequest = {
  phone_numbers: string[];
  subscriber: PortOrderSubscriber;
  requested_foc_date: string; // YYYY-MM-DD
  requested_foc_time?: string; // HH:MM, Eastern
};

export type UpdatePortOrderRequest = {
  id: number;
} & Partial<CreatePortOrderRequest>;

export type PortOrderProviderDetails = {
  phone_numbers: string[];
  subscriber?: PortOrderSubscriber;
  requested_foc_date?: string;
  requested_foc_time?: string;
  actual_foc_date?: string;
  losing_carrier?: { name: string; spid: string; port_type: string };
  rejection?: { code: string; message: string };
  approval?: { signature: string; ip: string; timestamp: string };
  loa?: { s3_key: string; content_type: string; file_size: number };
  bill_copy?: { s3_key: string; content_type: string; file_size: number };
  csr?: { s3_key: string; content_type: string; file_size: number };
};

export type PortOrder = {
  id: number;
  dialstack_port_order_id: string;
  numbers: string[];
  status: PortOrderStatus;
  requested_foc_at: string | null;
  actual_foc_at: string | null;
  last_exception_message: string | null;
  last_synced_at: string | null;
  sms_port_out_expires_at: string | null;
  created_at: string;
  created_by: { id: number; full_name: string } | null;
};

export type PortOrderDetail = PortOrder & {
  provider: {
    id: string;
    status: string;
    details: PortOrderProviderDetails;
    submitted_at: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  routing_warning?: string | null;
};

// Raw provider events (unwrapped `data`); only `type` is guaranteed.
export type PortOrderEvent = {
  type: string;
  id?: string;
  message?: string;
  created_at?: string;
};

// ENG-1916 PR 6: a clinic number's inbound routing, read and written from the
// Voice Setup page (Suno-only). `targets` is composed server-side — DialStack
// has no routing-targets endpoint. While calendar closures own the number
// (`closures_enabled`) the effective target is reported but cannot be changed.
export type VoiceRoutingTargetType =
  | "user"
  | "dial_plan"
  | "ring_group"
  | "shared_voicemail";

export type VoiceRoutingTarget = {
  id: string;
  name: string;
  type: VoiceRoutingTargetType;
  extension_number?: string | null;
};

export type VoiceClinicRouting = {
  clinic_id: number;
  phone_number: string;
  phone_number_id: string;
  routing_target: string | null;
  inbound_routing: string;
  caller_id_prefix: string | null;
  closures_enabled: boolean;
  targets: VoiceRoutingTarget[];
};

export type SetClinicRoutingRequest = {
  clinicId: number;
  routing_target: string | null;
  caller_id_prefix: string | null;
};

// ENG-1932 self-serve voice settings (admin-only backend proxy over the
// provider's routing config — schedules, greeting clips, dial plans, ring
// groups). Provider is the source of truth; nothing here is stored in Suno.

// day: 0 = Sunday … 6 = Saturday; times are 'HH:MM' in the schedule's timezone.
export type VoiceScheduleTimeRange = {
  day: number;
  start: string;
  end: string;
};

// Inclusive 'YYYY-MM-DD' dates.
export type VoiceScheduleDateRange = {
  start: string;
  end: string;
};

// Temporary override: value true forces open, false forces closed, until it
// auto-expires at `until` (RFC3339).
export type VoiceScheduleHold = {
  value: boolean;
  until: string;
};

export type VoiceSchedule = {
  id: string;
  name: string;
  timezone: string | null;
  ranges: VoiceScheduleTimeRange[];
  holidays: VoiceScheduleDateRange[];
  hold: VoiceScheduleHold | null;
  created_at: string;
  updated_at: string;
};

export type VoiceSchedulePayload = {
  name?: string;
  timezone?: string;
  ranges?: VoiceScheduleTimeRange[];
  holidays?: VoiceScheduleDateRange[];
};

// `url` is a short-lived signed link — always fetched fresh, never cached.
export type VoiceAudioClip = {
  id: string;
  name: string;
  url: string;
  mime_type: string;
  duration_ms: number | null;
  size_bytes: number;
  created_at: string;
  updated_at: string;
};

export type VoiceDialPlan = {
  id: string;
  name: string;
  entry_node?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type VoiceRingGroupMember = {
  id: string;
  /** DialStack routing-target id (e.g. `user_…`), NOT a dial code. */
  extension: string | null;
  phone_number: string | null;
  /** Teammate name resolved by the backend; null for external numbers. */
  display_name?: string | null;
  created_at?: string;
};

/** A teammate a ring group can ring. */
export type VoiceRingTarget = {
  id: string;
  name: string;
};

export type VoiceRingGroup = {
  id: string;
  name: string;
  timeout_seconds: number;
  ignore_forwarding?: boolean;
  confirm_external?: boolean;
  timeout_action?: "ring_user" | "voicemail" | "queue" | null;
  timeout_target?: string | null;
  members: VoiceRingGroupMember[];
  created_at?: string;
  updated_at?: string;
};

export type VoiceRingGroupPayload = {
  name?: string;
  timeout_seconds?: number;
  ignore_forwarding?: boolean;
  confirm_external?: boolean;
};

// ENG-1946 calendar-aware closures: while a clinic event is active, the
// clinic's number routes to a greeting + voicemail. Closing phones is opt-in —
// an event closes them only when an enabled keyword rule matches its title,
// staff pin a greeting on it, or the clinic opts into blanket mode
// (close_unmatched_events).

// Where closure callers leave messages: a shared box or a staff member's own
// mailbox. Derived from the clinic number's routing (the ring group's timeout
// mailbox, the user, or the box the number rings) unless an admin chose one.
export type VoiceClosureVoicemailDestination = {
  kind: "user" | "shared_voicemail";
  id: string;
  name: string;
};

export type VoiceClosureClinicStatus = {
  clinic_id: number;
  clinic_name: string;
  phone_number: string | null;
  enabled: boolean;
  close_unmatched_events: boolean;
  default_audio_clip_id: string | null;
  voicemail_destination:
    | (VoiceClosureVoicemailDestination & { chosen: boolean })
    | null;
};

// `suggested` is what the number's routing falls back to today (null when it
// has no voicemail of its own); `options` is every mailbox an admin may pick.
export type VoiceClosureVoicemailOptions = {
  current: (VoiceClosureVoicemailDestination & { chosen: boolean }) | null;
  suggested: VoiceClosureVoicemailDestination | null;
  options: VoiceClosureVoicemailDestination[];
};

export type VoiceClosureRule = {
  id: number;
  keyword: string;
  audio_clip_id: string;
  priority: number;
  enabled: boolean;
};

export type VoiceClosureRulePayload = {
  keyword: string;
  audio_clip_id: string;
  priority: number;
  enabled: boolean;
};

// One upcoming closure event with its resolved routing decision.
// `next_occurrence` is 'YYYY-MM-DD'; times are 'HH:MM' in the clinic timezone.
// `audio_clip_source` says where the greeting came from; null with
// will_close=true means no greeting — callers go straight to voicemail.
export type VoiceClosureEvent = {
  schedule_id: number;
  title: string;
  is_global_practice_event: boolean;
  next_occurrence: string;
  start_time: string;
  end_time: string;
  will_close: boolean;
  matched_rule_id: number | null;
  audio_clip_id: string | null;
  audio_clip_source: "rule" | "default" | "override" | null;
  is_excluded: boolean;
  override_audio_clip_id: string | null;
};

export type VoiceClosureEventsResponse = {
  enabled: boolean;
  close_unmatched_events: boolean;
  events: VoiceClosureEvent[];
};

// What a hide/unhide changes besides the call row: the backend discards (or
// restores) the call's phone chart note and its inbox items (ENG-2364).
const HIDDEN_CALL_COPY_TAGS = [
  "ChartNotes",
  "DialstackCallMetrics",
  "DialstackVoicemailUnreadCount",
  "DialstackPatientCalls",
  "PatientDialogs",
  "UnreadMessagesCount"
] as const;

export const dialstackApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getRecordingSettings: builder.query<RecordingSettings, number>({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/recording-settings/`,
        method: "GET"
      }),
      providesTags: ["DialstackRecordingSettings"]
    }),
    updateRecordingSettings: builder.mutation<
      RecordingSettings,
      UpdateRecordingSettingsRequest
    >({
      query: ({ practiceId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/recording-settings/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackRecordingSettings"]
    }),
    createDialstackAccountSession: builder.mutation<
      DialstackSession,
      CreateAccountSessionRequest
    >({
      query: (body) => ({
        url: "/dialstack/session/",
        method: "POST",
        body
      })
    }),
    createDialstackUserSession: builder.mutation<
      DialstackSession,
      { ttl_seconds?: number } | void
    >({
      query: (body) => ({
        url: "/dialstack/user-session/",
        method: "POST",
        body: body ?? {}
      }),
      // Minted on a timer by the softphone (token refresh, self-heal). A
      // single 401 here must not log the user out of Suno mid-call — see
      // baseQueryWithReauthFn (ENG-1966).
      extraOptions: { keepSessionOn401: true }
    }),
    // Records who a browser-SDK outbound dial is calling — the backend's
    // call.initiated webhook folds the real call into this row, so the /calls
    // page and patient tab attribute the patient. Fire-and-forget: the dial
    // itself never depends on this.
    registerOutboundCallContext: builder.mutation<
      DialstackCall,
      OutboundCallContextRequest
    >({
      query: (body) => ({
        url: "/dialstack/calls/outbound-context/",
        method: "POST",
        body
      })
    }),
    // The numbers this user may call from, and which one is the default.
    getOutboundCallerIds: builder.query<OutboundCallerIds, void>({
      query: () => ({ url: "/dialstack/caller-ids/", method: "GET" }),
      // Logging in on a desk phone changes `devices` (ENG-2293).
      providesTags: ["DialstackCallerIds"]
    }),
    // Hot desking (ENG-2293): the practice's desk phones, whose each is, and
    // where the requester stands.
    getDeskPhones: builder.query<DeskPhones, void>({
      query: () => ({ url: "/dialstack/desk-phones/", method: "GET" }),
      providesTags: ["DialstackDeskPhones"]
    }),
    logInOnDeskPhone: builder.mutation<DeskPhones, string>({
      query: (deviceId) => ({
        url: `/dialstack/desk-phones/${encodeURIComponent(deviceId)}/log-in/`,
        method: "POST"
      }),
      invalidatesTags: ["DialstackDeskPhones", "DialstackCallerIds"]
    }),
    logOutOfDeskPhone: builder.mutation<DeskPhones, void>({
      query: () => ({ url: "/dialstack/desk-phones/log-out/", method: "POST" }),
      invalidatesTags: ["DialstackDeskPhones", "DialstackCallerIds"]
    }),
    // Who logged in where and for how long: open sessions first, then newest
    // first; a flat limit/offset page. Admins only.
    getDeskPhoneHistory: builder.query<
      DeskPhoneHistoryRow[],
      DeskPhoneHistoryQuery
    >({
      query: (params) => {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(params))
          if (value !== undefined && value !== "")
            search.set(key, String(value));
        const query = search.toString();
        return {
          url: `/dialstack/desk-phones/history/${query ? `?${query}` : ""}`,
          method: "GET"
        };
      },
      providesTags: ["DialstackDeskPhones"]
    }),
    // Platform click-to-call: it rings this user's devices first, then dials
    // the destination on answer. The softphone's own WebRTC leg cannot carry a
    // per-call caller ID, so this is the only way to present a number other
    // than the standing one (ENG-1938) — and the only way to take a call
    // started in the app on the desk phone (`device: "deskphone"`, ENG-2183).
    initiateOutboundCall: builder.mutation<
      DialstackCall,
      InitiateOutboundCallRequest
    >({
      query: (body) => ({
        url: "/dialstack/calls/initiate/",
        method: "POST",
        body
      })
    }),
    // Patients whose numbers match a hand-dialed destination (same exact
    // last-10 semantics as inbound matching).
    getOutboundPatientMatch: builder.query<
      OutboundPatientMatch,
      { number: string }
    >({
      query: ({ number }) => ({
        url: `/dialstack/calls/patient-match/?number=${encodeURIComponent(number)}`,
        method: "GET"
      })
    }),
    // Colleagues with an extension, sorted by name; the requester is excluded.
    getVoiceDirectory: builder.query<VoiceDirectoryEntry[], void>({
      query: () => ({ url: "/dialstack/calls/directory/", method: "GET" })
    }),
    // Flat newest-first array (NoCountFlatPagination — no count envelope).
    getPatientCalls: builder.query<DialstackCall[], GetPatientCallsRequest>({
      query: ({ patientId, limit, offset }) => ({
        url: `/patients/${patientId}/calls/?limit=${limit}&offset=${offset}`,
        method: "GET"
      }),
      providesTags: ["DialstackPatientCalls"]
    }),
    // Flat newest-first array (NoCountFlatPagination); powers the practice /calls page.
    getPracticeCalls: builder.query<PracticeCall[], GetPracticeCallsRequest>({
      query: ({
        limit,
        offset,
        direction,
        status,
        patient_id,
        contact_id,
        from_date,
        to_date,
        search,
        assigned_tags,
        caller_type,
        intent,
        outcome,
        conversion,
        needs_action,
        is_opportunity,
        hidden
      }) => {
        const params = new URLSearchParams();
        Object.entries({
          limit,
          offset,
          direction,
          status,
          patient_id,
          contact_id,
          from_date,
          to_date,
          search,
          assigned_tags,
          needs_action,
          is_opportunity,
          ...(hidden ? { hidden: "true" } : {})
        }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        Object.entries({ caller_type, intent, outcome, conversion }).forEach(
          ([key, value]) => {
            if (value?.length) params.set(key, value.join(","));
          }
        );
        return {
          url: `/dialstack/calls/?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["DialstackCalls"]
    }),
    // The Call Intelligence page's single payload (ENG-2126). Same window and
    // filter contract as the metrics query.
    getDialstackCallInsights: builder.query<
      DialstackCallInsights,
      GetDialstackCallMetricsRequest
    >({
      query: ({ after_date, before_date, clinic, user }) => {
        const params = new URLSearchParams();
        Object.entries({ after_date, before_date }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        Object.entries({ clinic, user }).forEach(([key, value]) => {
          if (value?.length) params.set(key, value.join(","));
        });
        return {
          url: `/dialstack/calls/insights/?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["DialstackCallMetrics"]
    }),
    // Staff corrects the content classification (ENG-2126). Only the detail
    // is invalidated: list rows keep the compact insight until their next
    // authoritative refresh (the page-0 refetch footgun, SN-18212).
    correctCallInsight: builder.mutation<
      PracticeCallDetail,
      CorrectCallInsightRequest
    >({
      query: ({ callId, ...body }) => ({
        url: `/dialstack/calls/${callId}/insight/`,
        method: "POST",
        body
      }),
      invalidatesTags: (result, error, { callId }) => [
        { type: "DialstackCalls", id: callId }
      ]
    }),
    getPracticeCall: builder.query<PracticeCallDetail, number>({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/`,
        method: "GET"
      }),
      providesTags: (result, error, callId) => [
        { type: "DialstackCalls", id: callId }
      ]
    }),
    // Recording URLs are pre-signed and short-lived: never serve one from cache
    // (keepUnusedDataFor: 0), and refetch on audio error to get a fresh link.
    getPracticeCallRecording: builder.query<
      { recording_url: string | null },
      number
    >({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/recording/`,
        method: "GET"
      }),
      keepUnusedDataFor: 0
    }),
    // Pre-signed URL for the voicemail left on a call — practice-scoped, so the
    // front desk can play it from the call log regardless of mailbox ownership.
    // Never cached: the URL is short-lived (same contract as the recording).
    getCallVoicemailAudio: builder.query<{ audio_url: string | null }, number>({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/voicemail-audio/`,
        method: "GET"
      }),
      keepUnusedDataFor: 0
    }),
    // No tag invalidation: the workspace tracks dismissals locally (invalidating
    // the list would trip the usePagination page-0 refetch footgun, SN-18212).
    dismissCallFollowUp: builder.mutation<PracticeCall, number>({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/dismiss-follow-up/`,
        method: "POST"
      })
    }),
    // The note typed on a call (ENG-2226): the workbench autosaves it. Blank
    // clears it. The call detail and the chart notes it is mirrored into are
    // refetched; the paginated call lists are left alone (SN-18212).
    setCallNote: builder.mutation<SetCallNoteResponse, SetCallNoteRequest>({
      query: ({ callId, text }) => ({
        url: `/dialstack/calls/${callId}/note/`,
        method: "PUT",
        body: { text }
      }),
      invalidatesTags: (result, error, { callId }) => [
        { type: "DialstackCalls", id: callId },
        "ChartNotes"
      ]
    }),
    // Manually attach an unmatched call to a patient or a contact. The paginated
    // call lists are NOT invalidated (SN-18212): the response's attachment
    // fields go into the overlay slice instead, which every list applies via
    // `usePagination.updateItem`.
    attachCall: builder.mutation<AttachCallResponse, AttachCallRequest>({
      query: ({ callId, ...body }) => ({
        url: `/dialstack/calls/${callId}/attach/`,
        method: "POST",
        body
      }),
      async onQueryStarted({ callId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            attachmentApplied({
              callId,
              fields: pickAttachmentFields(data.call)
            })
          );
        } catch {
          // The caller surfaces the error; nothing to roll back.
        }
      },
      invalidatesTags: (result, error, { callId }) => [
        "DialstackPatientCalls",
        { type: "DialstackCalls", id: callId },
        "DialstackVoicemailUnreadCount",
        "ChartNotes",
        "PatientDialogs",
        "UnreadMessagesCount",
        ...(result?.number_remembered && result.call.patient
          ? [{ type: "Patients" as const, id: result.call.patient.id }]
          : [])
      ]
    }),
    // Takes a manual attachment off a call: the contact (ENG-2177) or the
    // patient (ENG-2563). The call keeps `matched_on: manual` so webhook
    // redelivery cannot re-match it; the overlay slice carries the cleared
    // fields to every loaded list row, as for attach. 409
    // `call_matched_by_number` for a patient the number itself named.
    detachCall: builder.mutation<DetachCallResponse, DetachCallRequest>({
      query: ({ callId, forget_number }) => ({
        url: `/dialstack/calls/${callId}/detach/`,
        method: "POST",
        body: forget_number ? { forget_number } : {}
      }),
      async onQueryStarted({ callId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            attachmentApplied({
              callId,
              fields: pickAttachmentFields(data.call)
            })
          );
        } catch {
          // The caller surfaces the error; nothing to roll back.
        }
      },
      // Removing a patient discards the phone note the attach filed, so the
      // chart's note list is refetched wherever it is open.
      invalidatesTags: (result, error, { callId }) => [
        { type: "DialstackCalls", id: callId },
        "DialstackContacts",
        { type: "ChartNotes" as const, id: "LIST" }
      ]
    }),
    // --- ENG-2364 hidden calls ---
    // Hide a call from the practice (business owners + Suno). The paginated
    // call lists are NOT invalidated (SN-18212): the hidden-calls overlay
    // drops the row from every loaded list; the chart, the overview numbers
    // and the inbox badge are refetched because the backend discarded the
    // call's phone note and inbox items.
    hideCall: builder.mutation<PracticeCallDetail, number>({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/hide/`,
        method: "POST",
        body: {}
      }),
      async onQueryStarted(callId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(callHidden({ callId }));
        } catch {
          // The caller surfaces the error; nothing to roll back.
        }
      },
      invalidatesTags: (result, error) => (error ? [] : HIDDEN_CALL_COPY_TAGS)
    }),
    // Put a hidden call back (Suno only).
    unhideCall: builder.mutation<PracticeCallDetail, number>({
      query: (callId) => ({
        url: `/dialstack/calls/${callId}/unhide/`,
        method: "POST",
        body: {}
      }),
      async onQueryStarted(callId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(callUnhidden({ callId }));
        } catch {
          // The caller surfaces the error; nothing to roll back.
        }
      },
      invalidatesTags: (result, error) => (error ? [] : HIDDEN_CALL_COPY_TAGS)
    }),
    // The in-call "Private" toggle: the backend resolves the live call. No
    // invalidation — nothing derived from a call exists until it ends, and the
    // hidden-calls slice records the answer (use-live-call-privacy.hook.ts).
    hideLiveCall: builder.mutation<PracticeCall, LiveCallRequest>({
      query: (body) => ({
        url: "/dialstack/calls/live/hide/",
        method: "POST",
        body
      })
    }),
    unhideLiveCall: builder.mutation<PracticeCall, LiveCallRequest>({
      query: (body) => ({
        url: "/dialstack/calls/live/unhide/",
        method: "POST",
        body
      })
    }),
    // A live call by the provider's call id (ENG-2380): the softphone's
    // fallback when no screen pop reached it for a ringing call, since the pop
    // is how it learns the backend Call id (use-pop-recovery.hook.ts). 404
    // until the webhook has written the row; a 404 is not retried here.
    getLiveCall: builder.query<PracticeCall, { dialstack_call_id: string }>({
      query: ({ dialstack_call_id }) => ({
        url: `/dialstack/calls/live/?dialstack_call_id=${encodeURIComponent(
          dialstack_call_id
        )}`,
        method: "GET"
      })
    }),
    // --- ENG-2177 Rolodex ---
    // Flat array (NoCountFlatPagination): page by limit/offset, stop on a
    // short page. The list tag is also invalidated by every contact mutation,
    // and the whole cache resets on a practice switch (use-switch-practice).
    getContacts: builder.query<DialstackContact[], GetContactsRequest>({
      query: ({ search, kind, favorite, ordering, limit, offset }) => {
        const params = new URLSearchParams();
        Object.entries({
          search,
          kind,
          favorite,
          ordering,
          limit,
          offset
        }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        return {
          url: `/dialstack/contacts/?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: (result) => [
        "DialstackContacts",
        ...(result ?? []).map(({ id }) => ({
          type: "DialstackContacts" as const,
          id
        }))
      ]
    }),
    // 404 when unknown, deleted, or another practice's.
    getContact: builder.query<DialstackContact, number>({
      query: (contactId) => ({
        url: `/dialstack/contacts/${contactId}/`,
        method: "GET"
      }),
      providesTags: (result, error, contactId) => [
        { type: "DialstackContacts", id: contactId }
      ]
    }),
    // 409 `number_belongs_to_contact` when a number is already another
    // contact's — nothing is saved.
    createContact: builder.mutation<DialstackContact, ContactInput>({
      query: (body) => ({
        url: "/dialstack/contacts/",
        method: "POST",
        body
      }),
      invalidatesTags: (result, error) => (error ? [] : ["DialstackContacts"])
    }),
    // No invalidation on a failed write (RTK runs invalidatesTags on errors
    // too): a 409 must leave the open dialog alone, not refetch under it.
    updateContact: builder.mutation<DialstackContact, ContactUpdateInput>({
      query: ({ id, ...body }) => ({
        url: `/dialstack/contacts/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: (result, error, { id }) =>
        error ? [] : ["DialstackContacts", { type: "DialstackContacts", id }]
    }),
    // Soft delete: history keeps the name, matching stops, numbers are freed.
    deleteContact: builder.mutation<void, number>({
      query: (contactId) => ({
        url: `/dialstack/contacts/${contactId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, contactId) => [
        "DialstackContacts",
        { type: "DialstackContacts", id: contactId }
      ]
    }),
    // Numbers, calls and voicemails of the source move to the target; the
    // source is removed. Responds with the merged target row.
    mergeContacts: builder.mutation<DialstackContact, MergeContactsRequest>({
      query: ({ targetId, source_id }) => ({
        url: `/dialstack/contacts/${targetId}/merge/`,
        method: "POST",
        body: { source_id }
      }),
      invalidatesTags: (result, error, { targetId, source_id }) =>
        error
          ? []
          : [
              "DialstackContacts",
              { type: "DialstackContacts", id: targetId },
              { type: "DialstackContacts", id: source_id },
              "DialstackCalls"
            ]
    }),
    // The requester's own speed dial — nobody else's star changes.
    setContactFavorite: builder.mutation<
      DialstackContact,
      SetContactFavoriteRequest
    >({
      query: ({ contactId, favorite }) => ({
        url: `/dialstack/contacts/${contactId}/favorite/`,
        method: favorite ? "POST" : "DELETE",
        ...(favorite ? { body: {} } : {})
      }),
      // The list tag too: the speed-dial read lists only starred rows, so a
      // newly starred contact is not among the ids it provides.
      invalidatesTags: (result, error, { contactId }) =>
        error
          ? []
          : ["DialstackContacts", { type: "DialstackContacts", id: contactId }]
    }),
    // Admin-only CSV of the Rolodex, filtered like the list (10k-row cap
    // server-side). Consumed lazily on click + downloadBlob, like the call log.
    exportContacts: builder.query<Blob, ExportContactsRequest>({
      query: ({ search, kind, favorite, ordering }) => {
        const params = new URLSearchParams();
        Object.entries({ search, kind, favorite, ordering }).forEach(
          ([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              params.set(key, String(value));
            }
          }
        );
        return {
          url: `/dialstack/contacts/export/?${params.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob()
        };
      }
    }),
    // Admin-only CSV import (multipart `file`, ≤5 MB, ≤2000 rows). Each row
    // is created on its own, so a 200 can still carry skipped rows; an
    // unusable file is a 400 `{detail}`.
    importContacts: builder.mutation<ContactImportResult, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/dialstack/contacts/import/",
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ["DialstackContacts"])
    }),
    getDialstackCallMetrics: builder.query<
      DialstackCallMetrics,
      GetDialstackCallMetricsRequest
    >({
      query: ({ after_date, before_date, clinic, user }) => {
        const params = new URLSearchParams();
        Object.entries({ after_date, before_date }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        Object.entries({ clinic, user }).forEach(([key, value]) => {
          if (value?.length) {
            params.set(key, value.join(","));
          }
        });
        return {
          url: `/dialstack/calls/metrics/?${params.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["DialstackCallMetrics"]
    }),
    // CSV download of the call log for the same window/filters as the metrics
    // (10,000-row cap server-side). Consumed lazily on click + downloadBlob.
    exportDialstackCalls: builder.query<Blob, ExportDialstackCallsRequest>({
      query: ({ from_date, to_date, clinic, user }) => {
        const params = new URLSearchParams();
        Object.entries({ from_date, to_date }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        Object.entries({ clinic, user }).forEach(([key, value]) => {
          if (value?.length) {
            params.set(key, value.join(","));
          }
        });
        return {
          url: `/dialstack/calls/export/?${params.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob()
        };
      }
    }),
    // Flat newest-first array (NoCountFlatPagination); the signed-in user's mailbox.
    getVoicemails: builder.query<Voicemail[], GetVoicemailsRequest>({
      query: ({
        limit,
        offset,
        unread,
        patient_id,
        contact_id,
        from_date,
        to_date,
        search,
        shared,
        box
      }) => {
        const params = new URLSearchParams();
        Object.entries({
          limit,
          offset,
          unread,
          patient_id,
          contact_id,
          from_date,
          to_date,
          search,
          shared,
          box
        }).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
          }
        });
        return {
          url: `/dialstack/voicemails/?${params.toString()}`,
          method: "GET"
        };
      }
    }),
    getVoicemailUnreadCount: builder.query<
      { unread_count: number },
      { shared?: boolean; box?: number } | void
    >({
      query: (request) => ({
        url: `/dialstack/voicemails/unread-count/${
          request?.box
            ? `?box=${request.box}`
            : request?.shared
              ? "?shared=true"
              : ""
        }`,
        method: "GET"
      }),
      providesTags: ["DialstackVoicemailUnreadCount"]
    }),
    // The shared boxes the signed-in user may read, with unread counts. The
    // counts ride the unread-count tag so read/delete refresh them too.
    getVoicemailBoxes: builder.query<SharedVoicemailBox[], void>({
      query: () => ({ url: "/dialstack/voicemail-boxes/", method: "GET" }),
      providesTags: ["DialstackVoicemailBoxes", "DialstackVoicemailUnreadCount"]
    }),
    // Admin: refresh the box list from DialStack (new boxes appear, deleted
    // ones go). The response is the refreshed list.
    syncVoicemailBoxes: builder.mutation<SharedVoicemailBox[], void>({
      query: () => ({
        url: "/dialstack/voicemail-boxes/sync/",
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["DialstackVoicemailBoxes"]
    }),
    // Admin: who may read one box.
    updateVoicemailBoxAccess: builder.mutation<
      SharedVoicemailBox,
      { boxId: number; restricted: boolean; member_ids: number[] }
    >({
      query: ({ boxId, restricted, member_ids }) => ({
        url: `/dialstack/voicemail-boxes/${boxId}/access/`,
        method: "POST",
        body: { restricted, member_ids }
      }),
      invalidatesTags: ["DialstackVoicemailBoxes"]
    }),
    // Heard/delete deliberately do NOT invalidate the voicemail list: the page
    // reads it through usePagination, where a page-0 refetch would wipe the
    // scrolled-in pages. Row state is tracked locally by the voicemails page;
    // only the cheap unread-count query is refetched.
    // The Heard button (ENG-2524): the backend stamps who and when. Playing a
    // voicemail never calls this.
    markVoicemailHeard: builder.mutation<Voicemail, number>({
      query: (voicemailId) => ({
        url: `/dialstack/voicemails/${voicemailId}/heard/`,
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["DialstackVoicemailUnreadCount"]
    }),
    deleteVoicemail: builder.mutation<void, number>({
      query: (voicemailId) => ({
        url: `/dialstack/voicemails/${voicemailId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoicemailUnreadCount"]
    }),
    // Voicemail audio URLs are pre-signed and short-lived: never serve one from
    // cache (keepUnusedDataFor: 0), and refetch on audio error for a fresh link.
    getVoicemailAudio: builder.query<{ audio_url: string | null }, number>({
      query: (voicemailId) => ({
        url: `/dialstack/voicemails/${voicemailId}/audio/`,
        method: "GET"
      }),
      keepUnusedDataFor: 0
    }),
    // The greeting object carries a short-lived signed URL: never serve it
    // from cache, and refetch after every change.
    getVoicemailGreeting: builder.query<
      { greeting: VoicemailGreeting | null },
      void
    >({
      query: () => ({ url: "/dialstack/voicemail-greeting/", method: "GET" }),
      providesTags: ["DialstackVoicemailGreeting"],
      keepUnusedDataFor: 0
    }),
    uploadVoicemailGreeting: builder.mutation<
      { greeting: VoicemailGreeting },
      FormData
    >({
      query: (body) => ({
        url: "/dialstack/voicemail-greeting/",
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoicemailGreeting"]
    }),
    deleteVoicemailGreeting: builder.mutation<void, void>({
      query: () => ({
        url: "/dialstack/voicemail-greeting/",
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoicemailGreeting"]
    }),
    // Admin: the greeting callers hear when they reach one shared box
    // (ENG-2525). Same contract as the personal greeting, keyed by box; the
    // signed URL is short-lived so the result is never served from cache.
    getVoicemailBoxGreeting: builder.query<
      { greeting: VoicemailGreeting | null },
      number
    >({
      query: (boxId) => ({
        url: `/dialstack/voicemail-boxes/${boxId}/greeting/`,
        method: "GET"
      }),
      providesTags: (_result, _error, boxId) => [
        { type: "DialstackVoicemailGreeting", id: `box-${boxId}` }
      ],
      keepUnusedDataFor: 0
    }),
    uploadVoicemailBoxGreeting: builder.mutation<
      { greeting: VoicemailGreeting },
      { boxId: number; body: FormData }
    >({
      query: ({ boxId, body }) => ({
        url: `/dialstack/voicemail-boxes/${boxId}/greeting/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { boxId }) => [
        { type: "DialstackVoicemailGreeting", id: `box-${boxId}` }
      ]
    }),
    deleteVoicemailBoxGreeting: builder.mutation<void, number>({
      query: (boxId) => ({
        url: `/dialstack/voicemail-boxes/${boxId}/greeting/`,
        method: "DELETE"
      }),
      invalidatesTags: (_result, _error, boxId) => [
        { type: "DialstackVoicemailGreeting", id: `box-${boxId}` }
      ]
    }),
    getVoiceSetupStatus: builder.query<VoiceSetupStatus, void>({
      query: () => ({ url: "/dialstack/setup/status/", method: "GET" }),
      providesTags: ["DialstackSetup"]
    }),
    attachDialstackAccount: builder.mutation<
      VoiceSetupStatus,
      { account_id: string | null }
    >({
      query: (body) => ({
        url: "/dialstack/setup/account/",
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackSetup", "DialstackRecordingSettings"]
    }),
    provisionVoiceAccount: builder.mutation<
      VoiceSetupProvisionResponse,
      VoiceSetupProvisionRequest
    >({
      query: (body) => ({
        url: "/dialstack/setup/account/provision/",
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackSetup", "DialstackRecordingSettings"]
    }),
    grantVoiceAccess: builder.mutation<
      VoiceSetupStatus,
      GrantVoiceAccessRequest
    >({
      query: ({ userId, ...body }) => ({
        url: `/dialstack/setup/users/${userId}/grant/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackSetup"]
    }),
    revokeVoiceAccess: builder.mutation<VoiceSetupStatus, { userId: number }>({
      query: ({ userId }) => ({
        url: `/dialstack/setup/users/${userId}/revoke/`,
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["DialstackSetup"]
    }),
    // The practice's feature switches (Suno-only). One key per flip; the
    // backend answers with the refreshed setup status.
    setVoiceFeatures: builder.mutation<VoiceSetupStatus, VoiceFeaturesRequest>({
      query: (body) => ({
        url: "/dialstack/setup/features/",
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackSetup"]
    }),
    // --- ENG-1916 number porting (Suno-only) ---
    checkPortEligibility: builder.mutation<
      PortEligibilityResult,
      { phone_numbers: string[] }
    >({
      query: (body) => ({
        url: "/dialstack/setup/port-orders/eligibility/",
        method: "POST",
        body
      })
    }),
    getPortOrders: builder.query<PortOrder[], void>({
      query: () => ({ url: "/dialstack/setup/port-orders/", method: "GET" }),
      providesTags: ["DialstackPortOrders"]
    }),
    getPortOrder: builder.query<PortOrderDetail, number>({
      query: (id) => ({
        url: `/dialstack/setup/port-orders/${id}/`,
        method: "GET"
      }),
      providesTags: ["DialstackPortOrders"]
    }),
    createPortOrder: builder.mutation<PortOrderDetail, CreatePortOrderRequest>({
      query: (body) => ({
        url: "/dialstack/setup/port-orders/",
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackPortOrders"]
    }),
    updatePortOrder: builder.mutation<PortOrderDetail, UpdatePortOrderRequest>({
      query: ({ id, ...body }) => ({
        url: `/dialstack/setup/port-orders/${id}/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackPortOrders"]
    }),
    approvePortOrder: builder.mutation<
      PortOrderDetail,
      { id: number; signature: string }
    >({
      query: ({ id, signature }) => ({
        url: `/dialstack/setup/port-orders/${id}/approve/`,
        method: "POST",
        body: { signature }
      }),
      invalidatesTags: ["DialstackPortOrders"]
    }),
    submitPortOrder: builder.mutation<PortOrderDetail, number>({
      query: (id) => ({
        url: `/dialstack/setup/port-orders/${id}/submit/`,
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["DialstackPortOrders"]
    }),
    cancelPortOrder: builder.mutation<PortOrderDetail, number>({
      query: (id) => ({
        url: `/dialstack/setup/port-orders/${id}/cancel/`,
        method: "POST",
        body: {}
      }),
      invalidatesTags: ["DialstackPortOrders"]
    }),
    uploadPortOrderBillCopy: builder.mutation<
      PortOrderDetail,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/dialstack/setup/port-orders/${id}/bill-copy/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["DialstackPortOrders"]
    }),
    getPortOrderEvents: builder.query<PortOrderEvent[], number>({
      query: (id) => ({
        url: `/dialstack/setup/port-orders/${id}/events/`,
        method: "GET"
      }),
      providesTags: ["DialstackPortOrders"]
    }),
    // --- ENG-1932 self-serve voice settings (admin-only) ---
    getVoiceSchedules: builder.query<{ schedules: VoiceSchedule[] }, number>({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/schedules/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceSchedules"]
    }),
    createVoiceSchedule: builder.mutation<
      VoiceSchedule,
      { practiceId: number } & VoiceSchedulePayload
    >({
      query: ({ practiceId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/schedules/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceSchedules"]
    }),
    updateVoiceSchedule: builder.mutation<
      VoiceSchedule,
      { practiceId: number; scheduleId: string } & VoiceSchedulePayload
    >({
      query: ({ practiceId, scheduleId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/schedules/${scheduleId}/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceSchedules"]
    }),
    deleteVoiceSchedule: builder.mutation<
      void,
      { practiceId: number; scheduleId: string }
    >({
      query: ({ practiceId, scheduleId }) => ({
        url: `/dialstack/practices/${practiceId}/schedules/${scheduleId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceSchedules"]
    }),
    // Temporary force-open/force-closed; `until` optional (provider defaults
    // to midnight tonight in the schedule's timezone), auto-expires.
    setVoiceScheduleHold: builder.mutation<
      VoiceSchedule,
      { practiceId: number; scheduleId: string; value: boolean; until?: string }
    >({
      query: ({ practiceId, scheduleId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/schedules/${scheduleId}/hold/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceSchedules"]
    }),
    clearVoiceScheduleHold: builder.mutation<
      void,
      { practiceId: number; scheduleId: string }
    >({
      query: ({ practiceId, scheduleId }) => ({
        url: `/dialstack/practices/${practiceId}/schedules/${scheduleId}/hold/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceSchedules"]
    }),
    // Clip URLs are pre-signed and short-lived: never serve them from cache.
    getVoiceAudioClips: builder.query<
      { audio_clips: VoiceAudioClip[] },
      number
    >({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/audio-clips/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceAudioClips"],
      keepUnusedDataFor: 0
    }),
    // multipart {name, file}; 5 MB cap enforced server-side too.
    uploadVoiceAudioClip: builder.mutation<
      VoiceAudioClip,
      { practiceId: number; body: FormData }
    >({
      query: ({ practiceId, body }) => ({
        url: `/dialstack/practices/${practiceId}/audio-clips/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceAudioClips"]
    }),
    renameVoiceAudioClip: builder.mutation<
      VoiceAudioClip,
      { practiceId: number; clipId: string; name: string }
    >({
      query: ({ practiceId, clipId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/audio-clips/${clipId}/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceAudioClips"]
    }),
    deleteVoiceAudioClip: builder.mutation<
      void,
      { practiceId: number; clipId: string }
    >({
      query: ({ practiceId, clipId }) => ({
        url: `/dialstack/practices/${practiceId}/audio-clips/${clipId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceAudioClips"]
    }),
    getVoiceDialPlans: builder.query<{ dial_plans: VoiceDialPlan[] }, number>({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/dial-plans/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceDialPlans"]
    }),
    createVoiceDialPlan: builder.mutation<
      VoiceDialPlan,
      { practiceId: number; name: string }
    >({
      query: ({ practiceId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/dial-plans/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceDialPlans"]
    }),
    deleteVoiceDialPlan: builder.mutation<
      void,
      { practiceId: number; dialPlanId: string }
    >({
      query: ({ practiceId, dialPlanId }) => ({
        url: `/dialstack/practices/${practiceId}/dial-plans/${dialPlanId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceDialPlans"]
    }),
    getVoiceRingGroups: builder.query<
      // `targets` is null when the backend could not reach the DialStack
      // user list (and absent from older backends).
      { ring_groups: VoiceRingGroup[]; targets?: VoiceRingTarget[] | null },
      number
    >({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceRingGroups"]
    }),
    createVoiceRingGroup: builder.mutation<
      VoiceRingGroup,
      { practiceId: number } & VoiceRingGroupPayload
    >({
      query: ({ practiceId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceRingGroups"]
    }),
    updateVoiceRingGroup: builder.mutation<
      VoiceRingGroup,
      { practiceId: number; ringGroupId: string } & VoiceRingGroupPayload
    >({
      query: ({ practiceId, ringGroupId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/${ringGroupId}/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceRingGroups"]
    }),
    deleteVoiceRingGroup: builder.mutation<
      void,
      { practiceId: number; ringGroupId: string }
    >({
      query: ({ practiceId, ringGroupId }) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/${ringGroupId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceRingGroups"]
    }),
    // Exactly one of extension (dial code) or phone_number (external).
    addVoiceRingGroupMember: builder.mutation<
      VoiceRingGroupMember,
      {
        practiceId: number;
        ringGroupId: string;
        extension?: string;
        phone_number?: string;
      }
    >({
      query: ({ practiceId, ringGroupId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/${ringGroupId}/members/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceRingGroups"]
    }),
    removeVoiceRingGroupMember: builder.mutation<
      void,
      { practiceId: number; ringGroupId: string; memberId: string }
    >({
      query: ({ practiceId, ringGroupId, memberId }) => ({
        url: `/dialstack/practices/${practiceId}/ring-groups/${ringGroupId}/members/${memberId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceRingGroups"]
    }),
    // --- ENG-1946 calendar-aware closures (admin-only) ---
    getVoiceClosures: builder.query<
      { clinics: VoiceClosureClinicStatus[]; rules: VoiceClosureRule[] },
      number
    >({
      query: (practiceId) => ({
        url: `/dialstack/practices/${practiceId}/closures/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceClosures"]
    }),
    getVoiceClosureVoicemailOptions: builder.query<
      VoiceClosureVoicemailOptions,
      { practiceId: number; clinicId: number }
    >({
      query: ({ practiceId, clinicId }) => ({
        url: `/dialstack/practices/${practiceId}/clinics/${clinicId}/closures/voicemail-options/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceClosures"]
    }),
    // Idempotent provision + enable. Omitted fields keep their stored values;
    // default_audio_clip_id: "" clears the default greeting. voicemail_target_id
    // picks an existing mailbox, new_voicemail_box_name creates a shared box;
    // neither keeps (or derives) the clinic's own voicemail destination.
    setupVoiceClosures: builder.mutation<
      VoiceClosureClinicStatus,
      {
        practiceId: number;
        clinicId: number;
        voicemail_target_id?: string;
        new_voicemail_box_name?: string;
        default_audio_clip_id?: string;
        close_unmatched_events?: boolean;
      }
    >({
      query: ({ practiceId, clinicId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/clinics/${clinicId}/closures/setup/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceClosures", "DialstackVoiceClosureEvents"]
    }),
    disableVoiceClosures: builder.mutation<
      VoiceClosureClinicStatus,
      { practiceId: number; clinicId: number }
    >({
      query: ({ practiceId, clinicId }) => ({
        url: `/dialstack/practices/${practiceId}/clinics/${clinicId}/closures/disable/`,
        method: "POST"
      }),
      invalidatesTags: ["DialstackVoiceClosures", "DialstackVoiceClosureEvents"]
    }),
    createVoiceClosureRule: builder.mutation<
      VoiceClosureRule,
      { practiceId: number } & VoiceClosureRulePayload
    >({
      query: ({ practiceId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/closures/rules/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceClosures", "DialstackVoiceClosureEvents"]
    }),
    updateVoiceClosureRule: builder.mutation<
      VoiceClosureRule,
      { practiceId: number; ruleId: number } & VoiceClosureRulePayload
    >({
      query: ({ practiceId, ruleId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/closures/rules/${ruleId}/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceClosures", "DialstackVoiceClosureEvents"]
    }),
    deleteVoiceClosureRule: builder.mutation<
      void,
      { practiceId: number; ruleId: number }
    >({
      query: ({ practiceId, ruleId }) => ({
        url: `/dialstack/practices/${practiceId}/closures/rules/${ruleId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["DialstackVoiceClosures", "DialstackVoiceClosureEvents"]
    }),
    getVoiceClosureEvents: builder.query<
      VoiceClosureEventsResponse,
      { practiceId: number; clinicId: number }
    >({
      query: ({ practiceId, clinicId }) => ({
        url: `/dialstack/practices/${practiceId}/clinics/${clinicId}/closures/events/`,
        method: "GET"
      }),
      providesTags: ["DialstackVoiceClosureEvents"]
    }),
    // Both fields cleared ({is_excluded: false, audio_clip_id: ""}) removes the
    // override and the event falls back to the keyword mapping.
    setVoiceClosureEventOverride: builder.mutation<
      VoiceClosureEvent,
      {
        practiceId: number;
        clinicId: number;
        scheduleId: number;
        is_excluded: boolean;
        audio_clip_id: string;
      }
    >({
      query: ({ practiceId, clinicId, scheduleId, ...body }) => ({
        url: `/dialstack/practices/${practiceId}/clinics/${clinicId}/closures/events/${scheduleId}/override/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackVoiceClosureEvents"]
    }),
    setClinicDialstackNumber: builder.mutation<
      VoiceSetupStatus,
      { clinicId: number; dialstack_phone_number: string | null }
    >({
      query: ({ clinicId, ...body }) => ({
        url: `/dialstack/setup/clinics/${clinicId}/number/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["DialstackSetup"]
    }),
    getClinicRouting: builder.query<VoiceClinicRouting, number>({
      query: (clinicId) => `/dialstack/setup/clinics/${clinicId}/routing/`,
      providesTags: (_result, _error, clinicId) => [
        { type: "DialstackClinicRouting", id: clinicId }
      ]
    }),
    setClinicRouting: builder.mutation<
      VoiceClinicRouting,
      SetClinicRoutingRequest
    >({
      query: ({ clinicId, ...body }) => ({
        url: `/dialstack/setup/clinics/${clinicId}/routing/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { clinicId }) => [
        { type: "DialstackClinicRouting", id: clinicId }
      ]
    })
  })
});

export const {
  useGetRecordingSettingsQuery,
  useUpdateRecordingSettingsMutation,
  useCreateDialstackAccountSessionMutation,
  useCreateDialstackUserSessionMutation,
  useRegisterOutboundCallContextMutation,
  useGetOutboundCallerIdsQuery,
  useGetDeskPhonesQuery,
  useLogInOnDeskPhoneMutation,
  useLogOutOfDeskPhoneMutation,
  useGetDeskPhoneHistoryQuery,
  useInitiateOutboundCallMutation,
  useGetOutboundPatientMatchQuery,
  useGetVoiceDirectoryQuery,
  useGetPatientCallsQuery,
  useGetPracticeCallsQuery,
  useGetPracticeCallQuery,
  useLazyGetPracticeCallQuery,
  useGetPracticeCallRecordingQuery,
  useGetCallVoicemailAudioQuery,
  useDismissCallFollowUpMutation,
  useCorrectCallInsightMutation,
  useGetDialstackCallInsightsQuery,
  useAttachCallMutation,
  useSetCallNoteMutation,
  useDetachCallMutation,
  useHideCallMutation,
  useUnhideCallMutation,
  useHideLiveCallMutation,
  useUnhideLiveCallMutation,
  useLazyGetLiveCallQuery,
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useMergeContactsMutation,
  useSetContactFavoriteMutation,
  useLazyExportContactsQuery,
  useImportContactsMutation,
  useGetDialstackCallMetricsQuery,
  useLazyExportDialstackCallsQuery,
  useGetVoicemailsQuery,
  useGetVoicemailUnreadCountQuery,
  useGetVoicemailBoxesQuery,
  useSyncVoicemailBoxesMutation,
  useUpdateVoicemailBoxAccessMutation,
  useMarkVoicemailHeardMutation,
  useDeleteVoicemailMutation,
  useGetVoicemailAudioQuery,
  useGetVoicemailGreetingQuery,
  useUploadVoicemailGreetingMutation,
  useDeleteVoicemailGreetingMutation,
  useGetVoicemailBoxGreetingQuery,
  useUploadVoicemailBoxGreetingMutation,
  useDeleteVoicemailBoxGreetingMutation,
  useGetVoiceSetupStatusQuery,
  useAttachDialstackAccountMutation,
  useProvisionVoiceAccountMutation,
  useGrantVoiceAccessMutation,
  useRevokeVoiceAccessMutation,
  useSetVoiceFeaturesMutation,
  useSetClinicDialstackNumberMutation,
  useCheckPortEligibilityMutation,
  useGetPortOrdersQuery,
  useGetPortOrderQuery,
  useCreatePortOrderMutation,
  useUpdatePortOrderMutation,
  useApprovePortOrderMutation,
  useSubmitPortOrderMutation,
  useCancelPortOrderMutation,
  useUploadPortOrderBillCopyMutation,
  useGetPortOrderEventsQuery,
  useGetClinicRoutingQuery,
  useSetClinicRoutingMutation,
  useGetVoiceSchedulesQuery,
  useCreateVoiceScheduleMutation,
  useUpdateVoiceScheduleMutation,
  useDeleteVoiceScheduleMutation,
  useSetVoiceScheduleHoldMutation,
  useClearVoiceScheduleHoldMutation,
  useGetVoiceClosuresQuery,
  useSetupVoiceClosuresMutation,
  useDisableVoiceClosuresMutation,
  useCreateVoiceClosureRuleMutation,
  useUpdateVoiceClosureRuleMutation,
  useDeleteVoiceClosureRuleMutation,
  useGetVoiceClosureEventsQuery,
  useGetVoiceClosureVoicemailOptionsQuery,
  useSetVoiceClosureEventOverrideMutation,
  useGetVoiceAudioClipsQuery,
  useUploadVoiceAudioClipMutation,
  useRenameVoiceAudioClipMutation,
  useDeleteVoiceAudioClipMutation,
  useGetVoiceDialPlansQuery,
  useCreateVoiceDialPlanMutation,
  useDeleteVoiceDialPlanMutation,
  useGetVoiceRingGroupsQuery,
  useCreateVoiceRingGroupMutation,
  useUpdateVoiceRingGroupMutation,
  useDeleteVoiceRingGroupMutation,
  useAddVoiceRingGroupMemberMutation,
  useRemoveVoiceRingGroupMemberMutation
} = dialstackApi;
